import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, loginWithGoogle } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

export default function AuthDialog({ open, onClose }) {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const reset = () => {
        setMode("login");
        setPassword("");
        setForgotSent(false);
    };

    const login = async () => {
        if (!email.trim() || !password) return toast.error("E-posta ve şifre zorunludur");
        setBusy(true);
        try {
            const r = await api.post("/auth/login-password", { email: email.trim().toLowerCase(), password });
            setUser(r.data);
            onClose();
            reset();
            navigate("/panel");
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Giriş yapılamadı");
        } finally {
            setBusy(false);
        }
    };

    const forgot = async () => {
        if (!email.trim()) return toast.error("E-posta adresinizi girin");
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
        } catch { /* anti-enumerasyon: her durumda aynı mesaj */ }
        setBusy(false);
        setForgotSent(true);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); reset(); } }}>
            <DialogContent className="max-w-sm" data-testid="auth-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{mode === "login" ? "Giriş Yap" : "Şifremi Unuttum"}</DialogTitle>
                    <DialogDescription>
                        {mode === "login" ? "Üye hesabınızla platforma giriş yapın." : "E-posta adresinize şifre sıfırlama bağlantısı gönderelim."}
                    </DialogDescription>
                </DialogHeader>

                {mode === "login" ? (
                    <div className="space-y-3">
                        <Button variant="outline" className="w-full" onClick={loginWithGoogle} data-testid="auth-google-button">
                            <span className="mr-2 flex h-4 w-4 items-center justify-center rounded-full bg-white text-xs font-bold text-stone-700">G</span>
                            Google ile Giriş Yap
                        </Button>
                        <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                            <span className="h-px flex-1 bg-border" /> veya e-posta ile <span className="h-px flex-1 bg-border" />
                        </div>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input type="email" placeholder="E-posta" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" data-testid="auth-email-input" />
                        </div>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                type={showPw ? "text" : "password"}
                                placeholder="Şifre"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && login()}
                                className="px-9"
                                data-testid="auth-password-input"
                            />
                            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="auth-toggle-password">
                                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        <Button onClick={login} disabled={busy} className="w-full bg-terra-600 text-white hover:bg-terra-700" data-testid="auth-login-submit">
                            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Giriş Yap
                        </Button>
                        <button onClick={() => setMode("forgot")} className="w-full text-center text-xs font-medium text-terra-600 hover:underline" data-testid="auth-forgot-link">
                            Şifremi Unuttum
                        </button>
                        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
                            Hesabınız başvuru onayı ile açılır. İlk şifrenizi "Şifremi Unuttum" ile e-postanıza gelen bağlantıdan belirleyebilirsiniz.
                        </p>
                    </div>
                ) : forgotSent ? (
                    <div className="space-y-4 py-2 text-center" data-testid="auth-forgot-success">
                        <Mail className="mx-auto h-10 w-10 text-sage-600" />
                        <p className="text-sm leading-relaxed">
                            <span className="font-semibold">{email}</span> adresi kayıtlıysa sıfırlama bağlantısı gönderildi. Bağlantı 1 saat geçerlidir — e-postanızı (ve spam klasörünü) kontrol edin.
                        </p>
                        <Button variant="outline" onClick={reset} className="w-full" data-testid="auth-back-login">Girişe Dön</Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input type="email" placeholder="E-posta" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" data-testid="auth-forgot-email" />
                        </div>
                        <Button onClick={forgot} disabled={busy} className="w-full bg-terra-600 text-white hover:bg-terra-700" data-testid="auth-forgot-submit">
                            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Sıfırlama Bağlantısı Gönder
                        </Button>
                        <button onClick={reset} className="w-full text-center text-xs font-medium text-muted-foreground hover:text-foreground" data-testid="auth-back-login-link">
                            ← Girişe dön
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
